import {
  NATUREZA_DA_SOLUCAO,
  ROTULO_NATUREZA_DA_SOLUCAO,
  ROTULO_PRIORIDADE,
  ROTULO_TIPO,
  TEXTOS,
} from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'
import { TabelaDeRotulos, type LinhaDoDocumento } from './TabelaDeRotulos'

/**
 * O que sai na célula quando o campo não veio.
 *
 * A API omite campo vazio (`default-property-inclusion: non_null`), então aqui chega
 * `undefined` e não `null` — por isso o teste é `?.trim() || …` e não `=== null`.
 *
 * @param valor o campo como a API o devolveu
 * @returns o valor, ou "Não informado"
 */
function texto(valor: string | null | undefined): string {
  return valor?.trim() || TEXTOS.naoInformado
}

/**
 * As treze linhas da identificação, na ordem exata do documento antigo — e uma décima quarta.
 *
 * A décima quarta, "Solução", é a declaração de definitiva ou paliativa que a contratada pediu no
 * Teste de uso 1. Entra no FIM da tabela, e não perto de "Tipo": as treze de cima continuam na
 * posição em que um RDM arquivado as tem, e a conferência linha a linha segue valendo.
 *
 * Três decisões merecem registro, porque não são cópia:
 *
 * 1. "Número da Mudança" traz o CÓDIGO da solicitação, não o número do chamado. No sistema
 *    anterior a GMUD não tinha identificador próprio e a linha repetia o chamado; aqui ela
 *    tem (`GMUD-2026-00042`), e é por ele que o registro é achado. O chamado continua onde
 *    já estava: na capa, ao lado do "Ticket", e no rodapé de todas as folhas.
 * 2. "Solicitante da Mudança" e "Responsável Técnico" trazem campos diferentes. A view do
 *    sistema anterior imprimia `$mudanca->responsavel` nas duas linhas, o que fazia o
 *    documento afirmar que quem pediu e quem executa são sempre a mesma pessoa.
 * 3. "Telefone de Contato" e "Necessário realizar comunicado" ficam em "Não informado". A
 *    V2 não modela nenhum dos dois. Tirar a linha mudaria a forma do documento que a
 *    fiscalização confere folha a folha, e preencher com um valor plausível seria pior:
 *    "Não" em "necessário realizar comunicado" é uma afirmação sobre a mudança, não um
 *    espaço em branco.
 *
 * @param gmud a solicitação impressa
 * @returns as linhas da tabela de identificação
 */
function linhasDaIdentificacao(gmud: DetalheGmud): LinhaDoDocumento[] {
  return [
    { rotulo: 'Número da Mudança', valor: gmud.codigo },
    { rotulo: 'Sistema', valor: texto(gmud.sistema) },
    { rotulo: 'Solicitante da Mudança', valor: texto(gmud.solicitante) },
    { rotulo: 'Responsável Técnico', valor: texto(gmud.responsavel) },
    { rotulo: 'Modulo', valor: texto(gmud.modulo) },
    { rotulo: 'Sub Modulo', valor: texto(gmud.subModulo) },
    { rotulo: 'Telefone de Contato', valor: TEXTOS.naoInformado },
    { rotulo: 'Finalidade', valor: texto(gmud.finalidade) },
    { rotulo: 'Tipo', valor: ROTULO_TIPO[gmud.tipo] ?? gmud.tipo },
    { rotulo: 'Prioridade', valor: ROTULO_PRIORIDADE[gmud.prioridade] ?? gmud.prioridade },
    { rotulo: 'Indisponibilidade Prevista', valor: gmud.indisponibilidadePrevista ? 'Sim' : 'Não' },
    { rotulo: 'Necessário realizar comunicado', valor: TEXTOS.naoInformado },
    { rotulo: 'HH Indisponibilidade', valor: texto(gmud.indisponibilidadeDuracao) },
    {
      rotulo: NATUREZA_DA_SOLUCAO.rotulo,
      valor: (gmud.naturezaDaSolucao && ROTULO_NATUREZA_DA_SOLUCAO[gmud.naturezaDaSolucao]) || TEXTOS.naoInformado,
    },
  ]
}

/**
 * A tabela "Identificação da Requisição de Mudança" — a primeira coisa da folha 2.
 *
 * Os rótulos estão copiados do documento antigo letra por letra, "Modulo" e "Sub Modulo"
 * sem acento inclusive. Não é descuido nem contradição com a regra de linguagem simples:
 * este é o único documento do sistema que tem de poder ser posto ao lado de um RDM
 * arquivado e conferido linha a linha. Acertar a acentuação aqui é mudar o formulário.
 *
 * @param props a solicitação impressa
 * @returns a tabela de identificação
 */
export function IdentificacaoDaMudanca({ gmud }: { gmud: DetalheGmud }) {
  return <TabelaDeRotulos titulo="Identificação da Requisição de Mudança" linhas={linhasDaIdentificacao(gmud)} />
}
